import React, { useState, useEffect } from 'react';
import { XMarkIcon, DevicePhoneMobileIcon } from '@heroicons/react/24/outline';
import { useTheme } from '../contexts/ThemeContext';

interface PWAInstallPromptProps {
  className?: string;
}

// Persistence keys + how long "Позже"/✕ silences the banner.
const SNOOZE_KEY = 'pwaInstallSnoozeUntil';
const INSTALLED_KEY = 'pwaInstalled';
const SNOOZE_DAYS = 30;

// Should we even consider showing the prompt right now? False if the app is
// already installed (standalone display-mode), the user accepted install
// earlier, or they snoozed it and the snooze hasn't expired.
function isSuppressed(): boolean {
  try {
    if (localStorage.getItem(INSTALLED_KEY) === '1') return true;
    // Already running as an installed PWA — nothing to prompt.
    const standalone = window.matchMedia?.('(display-mode: standalone)')?.matches
      || (window.navigator as any).standalone === true;
    if (standalone) return true;
    const snoozeUntil = Number(localStorage.getItem(SNOOZE_KEY) || 0);
    if (snoozeUntil && Date.now() < snoozeUntil) return true;
  } catch {
    /* localStorage unavailable — fall through, show prompt */
  }
  return false;
}

const PWAInstallPrompt: React.FC<PWAInstallPromptProps> = ({ className = '' }) => {
  const [deferredPrompt, setDeferredPrompt] = useState<any>(null);
  const [showInstallButton, setShowInstallButton] = useState(false);
  const { theme } = useTheme();

  useEffect(() => {
    const handler = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e);
      // Honour a remembered choice — don't nag on every refresh.
      if (!isSuppressed()) {
        setShowInstallButton(true);
      }
    };

    // If the app gets installed (via our button or the browser's own UI),
    // remember it permanently so we never prompt again.
    const installedHandler = () => {
      try { localStorage.setItem(INSTALLED_KEY, '1'); } catch { /* ignore */ }
      setShowInstallButton(false);
      setDeferredPrompt(null);
    };

    window.addEventListener('beforeinstallprompt', handler);
    window.addEventListener('appinstalled', installedHandler);

    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
      window.removeEventListener('appinstalled', installedHandler);
    };
  }, []);

  const snooze = () => {
    try {
      localStorage.setItem(SNOOZE_KEY, String(Date.now() + SNOOZE_DAYS * 24 * 60 * 60 * 1000));
    } catch { /* ignore */ }
  };

  const handleInstallClick = async () => {
    if (!deferredPrompt) return;

    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;

    if (outcome === 'accepted') {
      // appinstalled will also fire, but set it here too for browsers that
      // don't emit that event reliably.
      try { localStorage.setItem(INSTALLED_KEY, '1'); } catch { /* ignore */ }
    } else {
      // Declined the native dialog — treat like "later" so we stop nagging.
      snooze();
    }

    setDeferredPrompt(null);
    setShowInstallButton(false);
  };

  const handleDismiss = () => {
    snooze();
    setShowInstallButton(false);
    setDeferredPrompt(null);
  };

  if (!showInstallButton) return null;

  return (
    <div className={`fixed top-4 left-4 right-4 lg:top-4 lg:right-4 lg:left-auto z-50 ${
      theme === 'dark' ? 'bg-gray-800' : 'bg-white'
    } rounded-lg shadow-lg border ${
      theme === 'dark' ? 'border-gray-700' : 'border-gray-200'
    } p-4 max-w-sm ${className}`}>
      
      {/* Header */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center space-x-2">
          <DevicePhoneMobileIcon className={`h-6 w-6 ${
            theme === 'dark' ? 'text-primary-400' : 'text-primary-600'
          }`} />
          <h3 className={`text-sm font-medium ${
            theme === 'dark' ? 'text-white' : 'text-gray-900'
          }`}>
            Установить приложение
          </h3>
        </div>
        <button
          onClick={handleDismiss}
          className={`p-1 rounded-md transition-colors ${
            theme === 'dark'
              ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-700'
              : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
          }`}
        >
          <XMarkIcon className="h-4 w-4" />
        </button>
      </div>

      {/* Content */}
      <p className={`text-sm mb-4 ${
        theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
      }`}>
        Установите GGDesk CRM на ваше устройство для быстрого доступа и работы офлайн.
      </p>

      {/* Buttons */}
      <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-2">
        <button
          onClick={handleInstallClick}
          className={`flex-1 px-4 py-3 text-sm font-medium rounded-lg transition-colors ${
            theme === 'dark'
              ? 'bg-primary-600 text-white hover:bg-primary-700 focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 focus:ring-offset-gray-800'
              : 'bg-primary-600 text-white hover:bg-primary-700 focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 focus:ring-offset-white'
          }`}
        >
          Установить
        </button>
        <button
          onClick={handleDismiss}
          className={`flex-1 px-4 py-3 text-sm font-medium rounded-lg transition-colors ${
            theme === 'dark'
              ? 'bg-gray-700 text-gray-300 hover:bg-gray-600 focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 focus:ring-offset-gray-800'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200 focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 focus:ring-offset-white'
          }`}
        >
          Позже
        </button>
      </div>

      {/* Mobile hint */}
      <div className="mt-3 text-xs text-center">
        <p className={`${
          theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
        }`}>
          💡 На мобильных устройствах используйте меню браузера для установки
        </p>
      </div>
    </div>
  );
};

export default PWAInstallPrompt;
